/**
 * /sources/signal/import — backfill older history into a connected Signal
 * account.
 *
 * Gated on a connected Signal — a linked BYO account OR the shared number
 * (whose connection shows up as bound groups, not a workspace-owned account) —
 * because history attaches to a source someone actually connected. Arriving
 * with neither lands on the connect flow instead of an upload box.
 *
 * Not per-collection like the WhatsApp equivalent (/sources/$collectionId/
 * import): one Signal backup carries every chat, so the importer itself is
 * where chats get picked.
 */
import { useQuery } from '@tanstack/react-query';
import { Link, Navigate, useNavigate } from '@tanstack/react-router';
import { ChevronLeft } from 'lucide-react';
import { SignalImport } from '@/components/onboarding/signal-import';
import { PageHeader } from '@/components/patterns/page-header';
import { Skeleton } from '@/components/ui/skeleton';
import { signalAccountApi, signalSharedApi } from '@/lib/api';
import { qk, queryClient } from '@/lib/query-client';

export function SignalImportPage() {
  const navigate = useNavigate();
  const accounts = useQuery({
    queryKey: qk.signalAccounts,
    queryFn: () => signalAccountApi.list(),
    // Signal may be unconfigured (503) — that reads as "nothing linked".
    retry: false,
  });
  // A shared-number workspace owns NO Signal account (the shared account lives
  // outside any workspace), so its connection shows up as bound groups, not an
  // account. Checking accounts alone bounced every shared-number user to
  // /sources/add even though their Signal is live and indexing.
  const sharedGroups = useQuery({
    queryKey: qk.signalGroups,
    queryFn: () => signalSharedApi.groups(),
    retry: false,
  });

  /** The import created collections — refresh the lists that show them. */
  const handleDone = () => {
    void queryClient.invalidateQueries({ queryKey: qk.imports });
    void navigate({ to: '/sources' });
  };

  if (accounts.isLoading || sharedGroups.isLoading) {
    return (
      <div className="mx-auto max-w-[640px] space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  // Connected via a linked BYO account OR the shared number (bound groups).
  const connected =
    (accounts.data ?? []).some((a) => a.status === 'working') ||
    (sharedGroups.data ?? []).some((g) => g.status !== 'removed');

  if (!connected) {
    return <Navigate to="/sources/add" replace />;
  }

  return (
    <div className="mx-auto max-w-[640px]">
      <Link
        to="/sources"
        className="inline-flex items-center gap-1 text-[13px] text-faint transition-colors hover:text-foreground"
      >
        <ChevronLeft className="h-3.5 w-3.5" />
        Back to Sources
      </Link>

      <PageHeader
        className="mt-4"
        title="Import Signal history"
        subtitle="Backfill conversations from before you linked Signal. Your linked account keeps indexing new messages either way."
      />

      <div className="mt-8">
        <SignalImport onDone={handleDone} />
      </div>
    </div>
  );
}
